import React, { useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { CashMovement, CategoriesResponse, ClassificationHistory, Debt } from "@/lib/types";
import { money, fmtDateTime } from "@/lib/format";
import { GroupBadge } from "@/components/shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, Loader2, History, Save } from "lucide-react";
import { DateEditInline } from "@/components/DateEditInline";

interface Props {
  movementId: string | null;
  categories: CategoriesResponse | null;
  debts: Debt[];
  onClose: () => void;
  onClassified: () => void;
}

const CashMovementDetailPanel: React.FC<Props> = ({ movementId, categories, debts, onClose, onClassified }) => {
  const [movement, setMovement] = useState<CashMovement | null>(null);
  const [history, setHistory] = useState<ClassificationHistory[]>([]);
  const [category, setCategory] = useState("");
  const [relatedDebt, setRelatedDebt] = useState("");
  const [note, setNote] = useState("");
  const [cstatus, setCstatus] = useState("manual");
  const [tags, setTags] = useState<Record<string, string>>({ area: "", periodicidad: "", criticidad: "", trazabilidad: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const catLabel = (key: string): string => {
    if (!categories) return key;
    for (const items of Object.values(categories.groups)) {
      const f = items.find((i) => i.key === key);
      if (f) return f.label;
    }
    return key;
  };

  const isDebtCategory = category
    ? categories?.groups.deuda_financiamiento.some((c) => c.key === category)
    : false;

  useEffect(() => {
    if (!movementId) return;
    setLoading(true);
    Promise.all([
      client.get(`/cash/movements/${movementId}`),
      client.get(`/cash/movements/${movementId}/classification-history`),
    ])
      .then(([m, h]) => {
        setMovement(m.data);
        setHistory(h.data);
        setCategory(m.data.category === "unclassified" ? "" : m.data.category);
        setRelatedDebt(m.data.related_debt_id || "");
        setCstatus(m.data.classification_status === "pendiente" ? "manual" : m.data.classification_status || "manual");
        setTags({
          area: m.data.tags?.area || "",
          periodicidad: m.data.tags?.periodicidad || "",
          criticidad: m.data.tags?.criticidad || "",
          trazabilidad: m.data.tags?.trazabilidad || "",
        });
        setNote("");
      })
      .finally(() => setLoading(false));
  }, [movementId]);

  const save = async () => {
    if (!movementId || !category) {
      toast.error("Selecciona una categoría");
      return;
    }
    setSaving(true);
    try {
      const body: any = { category, note, classification_status: cstatus, tags };
      if (isDebtCategory) {
        if (!relatedDebt) {
          toast.error("La clasificación de deuda requiere seleccionar una deuda");
          setSaving(false);
          return;
        }
        body.related_debt_id = relatedDebt;
      }
      await client.patch(`/cash/movements/${movementId}/classify`, body);
      toast.success("Movimiento clasificado");
      // reload history + movement
      const [m, h] = await Promise.all([
        client.get(`/cash/movements/${movementId}`),
        client.get(`/cash/movements/${movementId}/classification-history`),
      ]);
      setMovement(m.data);
      setHistory(h.data);
      onClassified();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={!!movementId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto rounded-none border-l border-slate-200 p-0 sm:max-w-[440px]" data-testid="movement-detail-panel">
        <SheetHeader className="border-b border-slate-100 p-6">
          <SheetTitle className="text-lg font-bold tracking-tight">Detalle de movimiento</SheetTitle>
          <SheetDescription>Clasifica el movimiento y consulta su historial y auditoría.</SheetDescription>
        </SheetHeader>

        {loading || !movement ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
          </div>
        ) : (
          <div className="space-y-6 p-6">
            {/* Summary */}
            <div className="border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {movement.type === "inflow" ? (
                    <ArrowDownToLine className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
                  ) : (
                    <ArrowUpFromLine className="h-5 w-5 text-red-600" strokeWidth={1.5} />
                  )}
                  <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
                    {movement.type === "inflow" ? "Entrada" : "Salida"}
                  </span>
                </div>
                <span
                  className={`text-xl font-medium tabular ${
                    movement.type === "inflow" ? "text-emerald-600" : "text-red-600"
                  }`}
                  data-testid="movement-amount"
                >
                  {movement.type === "inflow" ? "+" : "−"}
                  {money(movement.amount)}
                </span>
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="text-slate-800">{movement.description}</div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="movement-date">
                  {movement.source} ·{" "}
                  {movement.provider === "manual" ? (
                    <DateEditInline
                      date={movement.date}
                      testid="movement-date"
                      onSave={async (d) => {
                        const r = await client.patch(`/cash/movements/${movement.id}/date`, { date: d });
                        setMovement(r.data);
                        onClassified();
                      }}
                    />
                  ) : (
                    fmtDateTime(movement.date)
                  )}
                </div>
                {movement.reference && (
                  <div className="font-mono text-xs text-slate-400">Ref: {movement.reference}</div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-slate-400">Clasificación actual:</span>
                {movement.status === "unclassified" ? (
                  <GroupBadge group="pendientes" label="Sin clasificar" />
                ) : (
                  <GroupBadge group={movement.group} label={catLabel(movement.category)} />
                )}
              </div>
            </div>

            {/* Classification form */}
            <div>
              <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Clasificación manual
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Categoría</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="classify-category-select">
                      <SelectValue placeholder="Selecciona categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories &&
                        Object.entries(categories.groups).map(([g, items]) => (
                          <SelectGroup key={g}>
                            <SelectLabel>{categories.group_labels[g]}</SelectLabel>
                            {items
                              .filter((it) => it.key !== "unclassified")
                              .map((it) => (
                                <SelectItem key={it.key} value={it.key}>
                                  {it.label}
                                </SelectItem>
                              ))}
                          </SelectGroup>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Estado de clasificación</Label>
                  <Select value={cstatus} onValueChange={setCstatus}>
                    <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="classify-status-select">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.classification_states
                        .filter((s) => s.key !== "pendiente")
                        .map((s) => (
                          <SelectItem key={s.key} value={s.key}>
                            {s.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {isDebtCategory && (
                  <div className="border-l-2 border-amber-300 bg-amber-50 p-3">
                    <Label className="text-xs text-amber-700">Deuda relacionada (obligatoria)</Label>
                    <Select value={relatedDebt} onValueChange={setRelatedDebt}>
                      <SelectTrigger className="mt-1.5 rounded-none border-amber-300 bg-white" data-testid="classify-debt-select">
                        <SelectValue placeholder="Selecciona la deuda" />
                      </SelectTrigger>
                      <SelectContent>
                        {debts.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.creditor} · {money(d.outstanding)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-xs text-slate-500">Nota / justificación</Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="classify-note"
                    placeholder="Motivo de la clasificación (queda auditado)"
                    className="mt-1.5 rounded-none border-slate-300"
                    rows={2}
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Etiquetas</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    <Select value={tags.area} onValueChange={(v) => setTags({ ...tags, area: v })}>
                      <SelectTrigger className="rounded-none border-slate-300 text-xs" data-testid="tag-area">
                        <SelectValue placeholder="Área" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operacion">Operación</SelectItem>
                        <SelectItem value="administracion">Administración</SelectItem>
                        <SelectItem value="direccion">Dirección</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={tags.periodicidad} onValueChange={(v) => setTags({ ...tags, periodicidad: v })}>
                      <SelectTrigger className="rounded-none border-slate-300 text-xs" data-testid="tag-periodicidad">
                        <SelectValue placeholder="Periodicidad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unico">Único</SelectItem>
                        <SelectItem value="recurrente">Recurrente</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={tags.criticidad} onValueChange={(v) => setTags({ ...tags, criticidad: v })}>
                      <SelectTrigger className="rounded-none border-slate-300 text-xs" data-testid="tag-criticidad">
                        <SelectValue placeholder="Criticidad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="baja">Baja</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={tags.trazabilidad} onValueChange={(v) => setTags({ ...tags, trazabilidad: v })}>
                      <SelectTrigger className="rounded-none border-slate-300 text-xs" data-testid="tag-trazabilidad">
                        <SelectValue placeholder="Soporte" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="con_soporte">Con soporte</SelectItem>
                        <SelectItem value="sin_soporte">Sin soporte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="classify-submit"
                  className="flex w-full items-center justify-center gap-2 rounded-none bg-[#DD0000] py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
                  Guardar clasificación
                </button>
              </div>
            </div>

            {/* History */}
            <div>
              <h4 className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
                <History className="h-3.5 w-3.5" /> Historial de clasificación
              </h4>
              {history.length === 0 ? (
                <p className="text-xs text-slate-400">Sin cambios registrados.</p>
              ) : (
                <div className="space-y-2" data-testid="classification-history">
                  {history.map((h) => (
                    <div key={h.id} className="border border-slate-100 bg-slate-50/50 p-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-400">{h.previous_category ? catLabel(h.previous_category) : "—"}</span>
                        <span className="text-slate-300">→</span>
                        <span className="font-medium text-slate-700">{catLabel(h.category)}</span>
                      </div>
                      {h.note && <div className="mt-1 text-slate-500">"{h.note}"</div>}
                      <div className="mt-1 text-slate-400">
                        {h.classified_by} · {fmtDateTime(h.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CashMovementDetailPanel;
